import { useEffect } from 'react';

interface PageMetadata {
    title: string;
    description: string;
    keywords?: string;
    canonicalUrl?: string;
}

export const usePageMetadata = (pageName: string) => {
    useEffect(() => {
        // Comprehensive page metadata for all public pages
        const pageMetadata: { [key: string]: PageMetadata } = {
            // Main Pages
            home: {
                title: 'Diasporawise - Premium Investment Platform for Nigerian Real Estate',
                description: 'Join thousands of investors building wealth through carefully curated Nigerian real estate opportunities. Secure, transparent, and profitable investments with Diasporawise.',
                keywords: 'Nigerian real estate investment, diaspora investment, property investment Nigeria, real estate crowdfunding, investment platform',
                canonicalUrl: 'https://diasporawise.com/'
            },
            about: {
                title: 'About Diasporawise - Revolutionizing Investment Opportunities',
                description: 'Learn about Diasporawise mission to democratize investment opportunities. Discover our story, values, and commitment to making premium investments accessible to everyone.',
                keywords: 'about Diasporawise, investment platform, company mission, team, values, investment democratization',
                canonicalUrl: 'https://diasporawise.com/about'
            },
            projects: {
                title: 'Investment Projects - Premium Real Estate Opportunities | Diasporawise',
                description: 'Explore curated investment projects across Nigerian real estate market. From residential to commercial properties, find opportunities that match your investment goals.',
                keywords: 'investment projects, real estate projects, Nigerian properties, investment opportunities, property investment',
                canonicalUrl: 'https://diasporawise.com/projects'
            },
            investment: {
                title: 'Investment Opportunities - Nigerian Real Estate Gateway | Diasporawise',
                description: 'Your gateway to Nigerian investments. Explore diverse real estate opportunities including residential, commercial, and fractional property investments.',
                keywords: 'investment opportunities, Nigerian real estate, property investment, real estate investing, investment gateway',
                canonicalUrl: 'https://diasporawise.com/investment'
            },
            blogs: {
                title: 'Investment Insights & Market Analysis | Diasporawise Blog',
                description: 'Stay updated with latest investment insights, market analysis, and expert strategies. Read our comprehensive blog for investment intelligence and market trends.',
                keywords: 'investment blog, market analysis, investment insights, real estate news, investment strategies, financial education',
                canonicalUrl: 'https://diasporawise.com/blogs'
            },
            contact: {
                title: 'Contact Diasporawise - Get Investment Support & Guidance',
                description: 'Get in touch with our investment experts. Contact Diasporawise for support, guidance, and answers to your investment questions.',
                keywords: 'contact Diasporawise, investment support, customer service, investment guidance, help center',
                canonicalUrl: 'https://diasporawise.com/contact'
            },
            services: {
                title: 'Investment Services - Comprehensive Solutions | Diasporawise',
                description: 'Discover comprehensive investment services designed for the diaspora community. From investment platforms to financial tools and educational resources.',
                keywords: 'investment services, financial services, diaspora solutions, investment platform, financial tools',
                canonicalUrl: 'https://diasporawise.com/services'
            },
            testimonials: {
                title: 'Customer Testimonials - Success Stories | Diasporawise',
                description: 'Read real success stories from our investors. Discover how Diasporawise has helped thousands build wealth through Nigerian real estate investments.',
                keywords: 'customer testimonials, success stories, investor reviews, real estate investment success, Diasporawise reviews',
                canonicalUrl: 'https://diasporawise.com/testimonials'
            },
            privacy: {
                title: 'Privacy Policy - Data Protection & Security | Diasporawise',
                description: 'Learn how Diasporawise protects your personal information and data. Our comprehensive privacy policy ensures your security and privacy.',
                keywords: 'privacy policy, data protection, security, personal information, GDPR compliance, data privacy',
                canonicalUrl: 'https://diasporawise.com/privacy-policy'
            },
            terms: {
                title: 'Terms of Service - Legal Terms & Conditions | Diasporawise',
                description: 'Read our terms of service and legal conditions for using Diasporawise investment platform. Understand your rights and responsibilities.',
                keywords: 'terms of service, legal terms, conditions, user agreement, platform terms, legal compliance',
                canonicalUrl: 'https://diasporawise.com/terms-of-service'
            },

            // Dynamic Pages
            projectDetails: {
                title: 'Project Details - Investment Opportunity | Diasporawise',
                description: 'Detailed information about this investment project. View project specifications, financial projections, and investment terms.',
                keywords: 'project details, investment project, real estate project, project information, investment opportunity'
            },
            blogDetails: {
                title: 'Investment Article - Expert Insights | Diasporawise',
                description: 'Read this comprehensive investment article with expert insights and market analysis. Stay informed with the latest investment trends.',
                keywords: 'investment article, expert insights, market analysis, investment education, financial knowledge'
            },
            myInvestments: {
                title: 'My Investments - Portfolio Dashboard | Diasporawise',
                description: 'Track your investment portfolio and monitor returns. View all your investments, performance metrics, and investment history.',
                keywords: 'my investments, portfolio, investment tracking, investment dashboard, portfolio management'
            },
            profile: {
                title: 'Profile Settings - Account Management | Diasporawise',
                description: 'Manage your account settings and profile information. Update your personal details and security preferences.',
                keywords: 'profile settings, account management, user profile, account settings, personal information'
            },

            // Investment Type Pages
            realEstateNigeria: {
                title: 'Real Estate Investment Nigeria - Premium Properties | Diasporawise',
                description: 'Invest in premium Nigerian real estate properties with high growth potential. Explore residential and commercial investment opportunities.',
                keywords: 'real estate investment Nigeria, Nigerian properties, property investment, real estate Nigeria, investment opportunities',
                canonicalUrl: 'https://diasporawise.com/investment/real-estate-investment-nigeria'
            },
            realEstateAbroad: {
                title: 'Real Estate Investing from Abroad - Global Access | Diasporawise',
                description: 'Seamlessly invest in Nigerian real estate from anywhere in the world. Global access to premium property investment opportunities.',
                keywords: 'real estate investing abroad, global investment, remote property investment, international real estate, cross-border investment',
                canonicalUrl: 'https://diasporawise.com/investment/real-estate-investing-from-abroad'
            },
            commercialProperty: {
                title: 'Commercial Property Investment Nigeria - High ROI | Diasporawise',
                description: 'Invest in commercial properties including offices, retail spaces, and industrial facilities. High rental yields and long-term returns.',
                keywords: 'commercial property investment, commercial real estate, office buildings, retail spaces, industrial properties',
                canonicalUrl: 'https://diasporawise.com/investment/commercial-property-investment-nigeria'
            },
            residentialProperty: {
                title: 'Residential Property Investment Nigeria - Stable Returns | Diasporawise',
                description: 'Invest in residential properties including apartments, houses, and luxury developments. Stable tenants and regular income.',
                keywords: 'residential property investment, residential real estate, apartments, houses, residential properties',
                canonicalUrl: 'https://diasporawise.com/investment/residential-property-investment-nigeria'
            },
            fractionalProperty: {
                title: 'Fractional Property Investment Nigeria - Low Entry Cost | Diasporawise',
                description: 'Own a fraction of premium properties with lower investment requirements. Shared ownership benefits and professional management.',
                keywords: 'fractional property investment, shared ownership, property fractions, low entry investment, property co-ownership',
                canonicalUrl: 'https://diasporawise.com/investment/fractional-property-investment-nigeria'
            },

            // Admin Pages
            ctaManagement: {
                title: 'CTA Management - Admin Dashboard | Diasporawise',
                description: 'Manage call-to-action elements and marketing campaigns. Admin dashboard for CTA management and optimization.',
                keywords: 'CTA management, admin dashboard, marketing campaigns, call to action, admin tools'
            },
            ctaAdd: {
                title: 'Add CTA - Create New Call-to-Action | Diasporawise',
                description: 'Create new call-to-action elements for marketing campaigns. Add and configure CTAs for better user engagement.',
                keywords: 'add CTA, create call to action, marketing tools, campaign management, CTA creation'
            },
            ctaEdit: {
                title: 'Edit CTA - Modify Call-to-Action | Diasporawise',
                description: 'Edit and modify existing call-to-action elements. Update CTA settings and configurations for optimal performance.',
                keywords: 'edit CTA, modify call to action, CTA editing, campaign optimization, CTA management'
            },
            solar: {
                title: 'Sustainable Solar Energy Solutions | Diasporawise',
                description: 'Harness the power of the sun with Diasporawise sustainable energy solutions. Integrated solar panels and smart storage for energy independence.',
                keywords: 'solar energy Nigeria, sustainable power, energy independence, solar panels, energy storage, clean energy',
                canonicalUrl: 'https://diasporawise.com/solar'
            }
        };

        const metadata = pageMetadata[pageName] || {
            title: 'Diasporawise - Premium Investment Platform',
            description: 'Join thousands of investors building wealth through carefully curated Nigerian real estate opportunities.',
            keywords: 'investment, real estate, Nigeria, diaspora, property investment'
        };

        // Set page title
        document.title = metadata.title;

        // Set meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', metadata.description);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'description';
            meta.content = metadata.description;
            document.head.appendChild(meta);
        }

        // Set meta keywords
        if (metadata.keywords) {
            const metaKeywords = document.querySelector('meta[name="keywords"]');
            if (metaKeywords) {
                metaKeywords.setAttribute('content', metadata.keywords);
            } else {
                const meta = document.createElement('meta');
                meta.name = 'keywords';
                meta.content = metadata.keywords;
                document.head.appendChild(meta);
            }
        }

        // Set Open Graph meta tags
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
            ogTitle.setAttribute('content', metadata.title);
        } else {
            const meta = document.createElement('meta');
            meta.setAttribute('property', 'og:title');
            meta.content = metadata.title;
            document.head.appendChild(meta);
        }

        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription) {
            ogDescription.setAttribute('content', metadata.description);
        } else {
            const meta = document.createElement('meta');
            meta.setAttribute('property', 'og:description');
            meta.content = metadata.description;
            document.head.appendChild(meta);
        }

        // Set Twitter Card meta tags
        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle) {
            twitterTitle.setAttribute('content', metadata.title);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'twitter:title';
            meta.content = metadata.title;
            document.head.appendChild(meta);
        }

        const twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (twitterDescription) {
            twitterDescription.setAttribute('content', metadata.description);
        } else {
            const meta = document.createElement('meta');
            meta.name = 'twitter:description';
            meta.content = metadata.description;
            document.head.appendChild(meta);
        }

        // Set Canonical URL
        const canonicalUrl = metadata.canonicalUrl || window.location.href;
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;

        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = canonicalUrl;

        // Update og:url to match canonical URL
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) {
            ogUrl.setAttribute('content', canonicalUrl);
        } else {
            const meta = document.createElement('meta');
            meta.setAttribute('property', 'og:url');
            meta.content = canonicalUrl;
            document.head.appendChild(meta);
        }

    }, [pageName]);
}; 